<template>
    <Button
        v-if="showDelete"
        :onClick="handleDelete"
        size="m"
        color="transparent"
        name="remove product"
        :noPadding="true"
        :ariaLabel="
            t({
                key: 'checkout.remove_product',
                values: [`${productType} ${product.Description}`],
            })
        "
        icon="Bin" />
</template>

<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import { computed, defineComponent, PropType } from 'vue';
    import { MainProduct } from '@shared/interfaces/product';
    import { useStore } from '@/store';
    import { ModalType } from '@/store/general/types';
    import { isNotNil } from '@shared/utils/value';
    import Button from '@ui/components/Button/Button.vue';
    import { ProductType } from '@/api/Cart/cart.interfaces';

    export default defineComponent({
        name: 'DeleteProduct',
        methods: { t },
        components: { Button },
        props: {
            product: {
                type: Object as PropType<MainProduct>,
                required: true,
            },
        },
        setup(props) {
            const store = useStore();

            const mainProducts = computed<MainProduct[]>(
                () => store.getters['cart/getMainProducts']
            );

            const productType = computed(() => {
                let key = null;
                switch (props.product.Type) {
                    case ProductType.Hotel:
                        key = 'shared.hotel_product';
                        break;
                    case ProductType.Flight:
                        key = 'shared.flight';
                        break;
                    case ProductType.Activity:
                        key = 'shared.activity_product';
                        break;
                    case ProductType.Ferry:
                        key = 'shared.ferry_product';
                        break;
                    default:
                        break;
                }
                return key ? `(${t({ key })})` : '';
            });

            const isCombinationLastProduct = (): boolean => {
                const combinationIds = mainProducts.value
                    .map((product) => product.CombinationId)
                    .filter(isNotNil);

                if (combinationIds.length === mainProducts.value.length) {
                    return combinationIds.every((id) => id === combinationIds[0]);
                }

                return false;
            };

            const showDelete = computed<boolean>(() => {
                const { length } = mainProducts.value;
                return length > 1 && !isCombinationLastProduct();
            });

            function handleDelete() {
                store.dispatch('general/setModal', {
                    type: ModalType.DeleteProduct,
                    product: props.product,
                });
            }

            return { showDelete, productType, handleDelete };
        },
    });
</script>

<style lang="scss">
    @forward './DeleteProduct.scss';
</style>
